// src/stores/user.js
import { defineStore } from 'pinia';
import router from '@/router';

// getCookie helper
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,
    isFetching: false,
    skipFetchOnNull: false,
  }),
  actions: {
    // 1) Login action
    async login(email, password) {
      try {
        const csrfToken = getCookie("csrftoken");
        const response = await fetch("/api/login/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken
          },
          credentials: "include",
          body: JSON.stringify({ email, password })
        });
        const data = await response.json();
        // if the response is not 2xx, throw so the component catch() sees the correct status
        if (!response.ok) {
          throw {
            response: {
              status: response.status,
              data: data,
              headers: response.headers
            }
          };
        }
        if (data.success) {
          this.skipFetchOnNull = false;
          this.user = data.user;
          if (this.user.is_vip) {
            router.push("/customer/profile");
          } else {
            router.push("/home");
          }
        } else {
          // server returned success: false (invalid credentials)
          this.user = null;
          throw {
            response: {
              status: 400,
              data: data
            }
          };
        }
      } catch (error) {
        console.error("Login request failed:", error);
        this.user = null;
        throw error;
      }
    },

    // 2) Fetch user (NO redirects here)
    async fetchUser() {
      if ((this.skipFetchOnNull && this.user === null) || this.isFetching) return;
      this.isFetching = true;
      try {
        const response = await fetch("/api/user/", { credentials: "include" });
        if (response.status === 401) {
          // session expired or not logged in
          this.user = null;
          return;
        }
        const data = await response.json();
        if (data && data.isAuthenticated) {
          // ensure active_bar is set
          if (!data.active_bar || data.active_bar === "") {
            data.active_bar = data.bar;
          }
          this.user = data;
        } else {
          this.user = null;
        }
      } catch (error) {
        console.error("Error fetching user:", error);
        this.user = null;
      } finally {
        this.isFetching = false;
      }
    },

    // 3) Switch bar (local only)
    switchBar(newBar) {
      if (this.user) {
        this.user.active_bar = newBar;
      }
    },

    // 4) Logout
    async logout() {
      this.skipFetchOnNull = true;
      try {
        const csrfToken = getCookie("csrftoken");
        await fetch("/api/logout/", {
          method: "POST",
          headers: { "X-CSRFToken": csrfToken },
          credentials: "include"
        });
      } catch (error) {
        console.error("Error during logout:", error);
      }
      this.user = null;
      router.push("/home");
    },

    async deleteAccount(password) {
      const csrfToken = getCookie("csrftoken");
      const response = await fetch("/api/account/delete/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken
        },
        credentials: "include",
        body: JSON.stringify({ password })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw {
          response: {
            status: response.status,
            data,
          }
        };
      }

      this.skipFetchOnNull = true;
      this.user = null;
      router.push("/home");
      return data;
    }
  }
});
